/**
 * Search & RAG playground. The user picks a tenant and the server uses the
 * stored Tenant Reader key to run queries without exposing key material.
 */

import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Search, Sparkles, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { playgroundApi, type SearchChunk, type SearchResponse } from "@/lib/playground-api";
import { tenantApi, type Tenant } from "@/lib/proxy-api";
import { useHasInstance } from "@/lib/use-instance";
import { ApiException } from "@/lib/api-client";
import { useTenantWorkspace } from "@/lib/tenant-workspace";

export function PlaygroundSearchPage() {
  const hasInstance = useHasInstance();
  const workspace = useTenantWorkspace();
  const { data: tenantData } = useQuery({ queryKey: ["tenants"], queryFn: () => tenantApi.list(), enabled: hasInstance });
  const tenants: Tenant[] = tenantData?.business ?? [];
  const tenantOptions: Tenant[] = tenants.length > 0
    ? tenants
    : workspace.tenantId
      ? [{ id: workspace.tenantId, name: workspace.tenantName ?? `Tenant ${workspace.tenantId}` } as Tenant]
      : [];

  const [tenantId, setTenantId] = useState(() => workspace.isSuper ? "" : workspace.tenantId ?? "");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(10);
  const [semanticWeight, setSemanticWeight] = useState(0.9);
  const [useRag, setUseRag] = useState(false);
  const [answerInstructions, setAnswerInstructions] = useState("Answer the user's question using only the retrieved context. If the context is insufficient, say so.");
  const [results, setResults] = useState<SearchChunk[] | null>(null);
  const [answer, setAnswer] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!workspace.isSuper && workspace.tenantId && tenantId !== workspace.tenantId) {
      setTenantId(workspace.tenantId);
    }
  }, [tenantId, workspace.isSuper, workspace.tenantId]);

  const searchMut = useMutation({
    mutationFn: () =>
      useRag
        ? playgroundApi.chatStored({
            tenantId,
            query,
            limit,
            semantic_weight: semanticWeight,
            chat_prompt: query,
            system_messages: answerInstructions.trim() ? [answerInstructions.trim()] : undefined,
          })
        : playgroundApi.searchStored({
            tenantId,
            query,
            limit,
            semantic_weight: semanticWeight,
          }),
    onSuccess: (res: SearchResponse) => {
      setResults(res.results ?? res.chunks ?? []);
      setAnswer(res.answer ?? res.chat_response ?? null);
      setError(null);
    },
    onError: (err) => setError(err instanceof ApiException ? err.message : "Search failed"),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Search & RAG"
        description="Query the tenant's knowledge base using the stored Tenant Reader key."
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="pg-tenant">Tenant</Label>
            <select id="pg-tenant" className="h-9 min-w-48 rounded-md border border-input bg-transparent px-3 text-sm"
              value={tenantId} onChange={(e) => setTenantId(e.target.value)} disabled={!workspace.isSuper}>
              <option value="">Select…</option>
              {tenantOptions.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </CardContent>
      </Card>

      {tenantId && (
        <>
          {/* Query form */}
          <Card>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="q">Query</Label>
                <Input id="q" value={query} onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && query) searchMut.mutate(); }}
                  placeholder="Ask anything…" />
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="lim">Limit</Label>
                  <Input id="lim" type="number" min={1} max={50} value={limit}
                    onChange={(e) => setLimit(Number(e.target.value))} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="sw">Semantic weight</Label>
                  <Input id="sw" type="number" step={0.1} min={0} max={1} value={semanticWeight}
                    onChange={(e) => setSemanticWeight(Number(e.target.value))} />
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={useRag} onChange={(e) => setUseRag(e.target.checked)} />
                Generate RAG answer
              </label>
              {useRag && (
                <div className="space-y-1.5">
                  <Label htmlFor="cp">Answer instructions</Label>
                  <Input id="cp" value={answerInstructions} onChange={(e) => setAnswerInstructions(e.target.value)} />
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button onClick={() => searchMut.mutate()} disabled={!tenantId || !query || searchMut.isPending}>
                  {searchMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : useRag ? <Sparkles className="h-4 w-4" /> : <Search className="h-4 w-4" />}
                  {useRag ? "Search & generate" : "Search"}
                </Button>
                {error && <p className="text-sm text-destructive">{error}</p>}
              </div>
            </CardContent>
          </Card>

          {/* RAG answer */}
          {answer && (
            <Card className="border-primary/30 bg-primary/5">
              <CardContent>
                <p className="text-xs font-medium uppercase tracking-wide text-primary mb-2">RAG answer</p>
                <p className="text-sm whitespace-pre-wrap">{answer}</p>
              </CardContent>
            </Card>
          )}

          {/* Results */}
          {results && (
            <div className="space-y-3">
              <p className="text-sm font-medium">{results.length} result{results.length === 1 ? "" : "s"}</p>
              {results.length === 0 ? (
                <p className="text-sm text-muted-foreground">No chunks matched.</p>
              ) : (
                <div className="space-y-2">
                  {results.map((c, i) => <ChunkCard key={c.chunk_id ?? i} chunk={c} />)}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ChunkCard({ chunk }: { chunk: SearchChunk }) {
  return (
    <Card>
      <CardContent className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <span className="text-sm font-medium">{chunk.filename ?? chunk.document_id ?? "chunk"}</span>
          {chunk.score !== undefined && (
            <Badge variant="outline" className="font-mono text-xs">{chunk.score.toFixed(3)}</Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground line-clamp-4 whitespace-pre-wrap">{chunk.content}</p>
      </CardContent>
    </Card>
  );
}
